<template>
  <div>
    <label
      class="text-input block mb-2"
    >
      {{ $t('payout.calculation') }}
      <span class="font-normal">{{ `(${$t('pages.driver.payoutList.editPayoutModal.driverLanguage')}: ${$t(`language.${driver?.user?.language}`)})` }}</span>
    </label>
    <div class="flex flex-col gap-y-2">
      <div v-for="(entry, i) in internalEntries" :key="entry.id" class="flex gap-x-4">
        <multi-select
          v-model="entry.type"
          class="grow"
          :error="internalError?.[`entries[${i}]`]?.subject"
          :placeholder="$t('companyRewardType.label')"
          :options="fetchRewardTypes"
          :disabled="!(entry.enabled ?? false)"
          :can-deselect="false"
          :can-clear="false"
        />
        <FormInput
          v-model="entry.amount"
          :error="internalError?.[`entries[${i}]`]?.amount"
          type="number"
          class="w-1/4"
        />
        <div class="w-8 mr-2 flex item-center">
          <TrashIcon class="cursor-pointer" @click="deleteEntry(entry)" />
        </div>
      </div>
    </div>
    <div class="flex justify-center pt-5 pb-5">
      <div class="w-8">
        <AddIcon class="cursor-pointer" @click="addNew()" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import FormInput from '@utils/inputs/Input.vue';
import AddIcon from '@images/panes/add.svg?component';
import TrashIcon from '@images/trash.svg?component';
import { computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import type { Driver, CompanyPayoutEntry } from '@/entities/Entities';
import type { PartialOnly } from '@/utils/types';
import {useDeletePrompt} from '@utils/prompt/injects';
import MultiSelect from '@/components/utils/Multiselect.vue';
import { useI18n } from 'vue-i18n';
import { enumOptionsProvider } from '@/components/utils/table/hooks/provider';

interface Props {
  entries: Array<PartialOnly<CompanyPayoutEntry, '@id'|'@type'> & {enabled?: boolean}>;
  errors: unknown;
  driver: Driver | undefined;
}

const emit = defineEmits(['update:entries', 'update:errors']);
const props = defineProps<Props>();
const internalEntries = useVModel(props, 'entries', emit);
const internalError = useVModel(props, 'errors', emit);

const { t } = useI18n();

const total = computed(() => {
  const num = internalEntries.value.reduce((acc, entry) => acc + entry.amount, 0);
  if (typeof num !== 'number' || Math.abs(num) < 0.005) return (0).toFixed(2);

  return num.toFixed(2);
});

const addNew = () => {
  internalEntries.value.push({
    id: uuidv4(),
    payout: undefined,
    subject: '',
    comment: undefined,
    amount: 0,
    type: 'chf',
    enabled: true
  });
};

const prompt = useDeletePrompt();

const deleteEntry = async (entry: CompanyPayoutEntry) => {
  if (entry.type === 'manual' || await prompt?.()) {
    internalEntries.value = internalEntries.value.filter((e) => e.id !== entry.id);
  }
};

const fetchRewardTypes = enumOptionsProvider('/enums/LicenseRewardType', (value) => ({
  value,
  label: t(`companyRewardType.${value}`),
}));
</script>

<style lang="scss" scoped>
.total-box{
  @apply flex gap-x-4;
  @apply items-center;
  @apply pt-5;
  @apply border-t border-light-gray;
}
</style>
